import axios from 'axios';
import { URLEndpoints } from '../../constants/urlConstants';
const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL

const getInstanceUniqueId = (explicit = null) => {
  if (explicit) return explicit;
  if (typeof window === 'undefined') return null;

  const match = window.location.pathname.match(/\/instance\/([^/]+)/i);
  return match?.[1] || null;
};

const withInstanceQuery = (instanceUniqueId = null) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  return resolved ? { params: { instanceUniqueId: resolved } } : {};
};

const authHeaders = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return {};
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      accessToken: token,
    },
  };
};

export const createDecorator = async (payload = {}, instanceUniqueId = null) => {
  try {
    const resolved = getInstanceUniqueId(instanceUniqueId);
    const body = resolved ? { ...payload, instanceUniqueId: resolved } : payload;
    const res = await axios.post(`${BASE_URL}/${URLEndpoints.decorators}`, body);
    return res;
  } catch (error) {
    console.log(error);
  }
};

export const getDecoratorMatrixExport = async (instanceUniqueId = null) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  const config = authHeaders();
  config.params = resolved ? { instanceUniqueId: resolved } : {};

  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.decorators}/export/matrix`,
    config
  );
  return res?.data;
};

export const getSingleDecoratorDetails = async (
  id,
  instanceUniqueId = null,
  includeArchivedChargeId = null,
  chargeStatus = "active"
) => {
  try {
    const config = withInstanceQuery(instanceUniqueId);
    config.params = { ...(config.params || {}) };
    if (
      includeArchivedChargeId !== null &&
      includeArchivedChargeId !== undefined &&
      String(includeArchivedChargeId).trim() !== ''
    ) {
      config.params.includeArchivedChargeId = includeArchivedChargeId;
    }
    if (chargeStatus) config.params.chargeStatus = chargeStatus;

    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.decorators}/${id}`,
      config
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const getDecoratorsDetails = async (instanceUniqueId = null) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.decorators}`,
      withInstanceQuery(instanceUniqueId)
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const updateDecoratorDetails = async (id, payload = {}, instanceUniqueId = null) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  const body = resolved ? { ...payload, instanceUniqueId: resolved } : payload;
  const res = await axios.put(`${BASE_URL}/${URLEndpoints.decorators}/${id}`, body);
  return res?.data;
};

// Dedicated address endpoint: no charge matrix serialization or swallowed save errors.
export const getDecoratorReceivingAddressOptions = async (instanceUniqueId = null) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  if (!resolved) throw new Error('An instance must be selected to load receiving addresses.');
  const response = await axios.get(`${BASE_URL}/${URLEndpoints.decorators}/receiving-address-options`, {
    ...authHeaders(), params: { instanceUniqueId: resolved },
  });
  return response.data;
};

export const updateDecoratorReceivingAddress = async (decoratorId, selection, instanceUniqueId = null) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  if (!resolved) throw new Error('An instance must be selected to save this receiving address.');
  const response = await axios.patch(
    `${BASE_URL}/${URLEndpoints.decorators}/${decoratorId}/receiving-address`,
    { instanceUniqueId: resolved, selection },
    authHeaders()
  );
  return response.data;
};

export const deleteDecoratorDetails = async (id, instanceUniqueId = null) => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/${URLEndpoints.decorators}/${id}`,
      withInstanceQuery(instanceUniqueId)
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const deleteDecoratorCharge = async (
  decoratorId,
  chargeId,
  instanceUniqueId = null
) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  const config = authHeaders();
  config.params = resolved ? { instanceUniqueId: resolved } : {};

  const res = await axios.delete(
    `${BASE_URL}/${URLEndpoints.decorators}/${decoratorId}/charges/${chargeId}`,
    config
  );
  return res?.data;
};

export const setDecoratorChargeArchived = async (
  decoratorId,
  chargeId,
  archived,
  instanceUniqueId = null
) => {
  const resolved = getInstanceUniqueId(instanceUniqueId);
  const config = authHeaders();
  config.params = resolved ? { instanceUniqueId: resolved } : {};

  const res = await axios.patch(
    `${BASE_URL}/${URLEndpoints.decorators}/${decoratorId}/charges/${chargeId}/archive`,
    { archived: Boolean(archived) },
    config
  );
  return res?.data;
};

export const getChargeMaterialRules = async (decorationChargeId) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/production/charge-material-rules/${decorationChargeId}`,
      { ...authHeaders(), ...withInstanceQuery() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const saveChargeMaterialRules = async (decorationChargeId, rules = []) => {
  try {
    const res = await axios.put(
      `${BASE_URL}/production/charge-material-rules/${decorationChargeId}`,
      { rules },
      { ...authHeaders(), ...withInstanceQuery() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const getProductionMaterialsLite = async () => {
  try {
    const res = await axios.get(
      `${BASE_URL}/production/charge-material-rules/materials-lite`,
      { ...authHeaders(), ...withInstanceQuery() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
  }
};

export const getAuditRules = async ({ instanceId = null, decoratorId = null } = {}) => {
  try {
    const params = {};
    if (instanceId !== null && instanceId !== undefined && String(instanceId).trim() !== "") {
      params.instanceId = Number(instanceId);
    }
    if (decoratorId !== null && decoratorId !== undefined && String(decoratorId).trim() !== "") {
      params.decoratorId = Number(decoratorId);
    }

    const res = await axios.get(`${BASE_URL}/audit-rules`, {
      ...authHeaders(),
      params,
    });
    return res?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const createAuditRule = async (payload = {}) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/audit-rules`,
      payload,
      authHeaders()
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const updateAuditRule = async (id, payload = {}) => {
  try {
    const res = await axios.put(
      `${BASE_URL}/audit-rules/${id}`,
      payload,
      authHeaders()
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const deactivateAuditRule = async (id) => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/audit-rules/${id}`,
      authHeaders()
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
